import {
  useOversightRating,
  useRating,
} from '@risksmart-app/components/src/hooks/useRating';
import type { ControlType as Control_Type_Enum } from '@risksmart-app/domain/src/types/consts/control-type';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import { useMemo } from 'react';
import {
  getAllContributorsCellValue,
  getAllOwnersCellValue,
  getAllViewersCellValue,
} from 'src/rbac/contributorHelper';
import { notEmpty } from 'src/utilityTypes';
import { getTestScheduleStatus } from 'src/utils/table/utils/testScheduleStatusHelper';

import useEntityInfo from '@/hooks/getEntityInfo';
import { useCommonLookupLazy } from '@/hooks/useCommonLookupLazy';
import { getFriendlyId } from '@/utils/friendlyId';
import { calculateTrend } from '@/utils/trendCalculation';

import {
  calculateDesignEffectiveness,
  calculateOverallEffectiveness,
  calculatePerformanceEffectiveness,
} from './calculateEffectiveness';
import type { OversightEffectivenessByControl } from './hooks/useLatestOversightEffectivenessByControls';
import { UNRATED } from './lookupData';
import type { ControlFlatFields, ControlTableFields } from './types';

export const useLabelledFields = (
  records: ControlFlatFields[] | undefined,
  oversightEffectivenessByControl?: OversightEffectivenessByControl,
  canViewOversight: boolean = false
) => {
  const { getByValue: getCommonLookupByValue } = useCommonLookupLazy();
  const { getLabel: getLabelOverallEffectiveness } = useRating('effectiveness');
  const { getLabel: getLabelDesignEffectiveness } = useRating(
    'design_effectiveness'
  );
  const { getLabel: getLabelPerformanceEffectiveness } = useRating(
    'performance_effectiveness'
  );
  const { getLabel: getLabelOversightDesignEffectiveness } = useOversightRating(
    'design_effectiveness'
  );
  const { getLabel: getLabelOversightPerformanceEffectiveness } =
    useOversightRating('performance_effectiveness');
  const { getLabel: getLabelOversightOverallEffectiveness } =
    useOversightRating('effectiveness');
  const { getLabel: getLabelTestScheduleStatus } = useRating(
    'test_schedule_status'
  );
  const { getLabel: getEffectivenessTrendLabel } = useRating(
    'effectiveness_trend'
  );
  const getEntityInfo = useEntityInfo();

  return useMemo<ControlTableFields[] | undefined>(() => {
    return records?.map((d) => {
      const OverallEffectiveness = calculateOverallEffectiveness(d);
      const DesignEffectiveness = calculateDesignEffectiveness(d);
      const PerformanceEffectiveness = calculatePerformanceEffectiveness(d);

      // Only resolve oversight fields when the viewing gate is open. This keeps
      // ineligible users' row data free of an oversight-shaped structure (no
      // leakage in debug serialisations, audit logs, future "copy row JSON"
      // surfaces) and skips three per-row label lookups that would otherwise
      // resolve to UNRATED for nothing.
      const oversightFields = canViewOversight
        ? (() => {
            const oversightEntry = oversightEffectivenessByControl?.get(d.Id);
            const OversightDesignEffectiveness =
              oversightEntry?.DesignEffectiveness ?? null;
            const OversightPerformanceEffectiveness =
              oversightEntry?.PerformanceEffectiveness ?? null;
            const OversightOverallEffectiveness =
              oversightEntry?.OverallEffectiveness ?? null;

            return {
              OversightDesignEffectiveness,
              OversightDesignEffectivenessLabelled:
                OversightDesignEffectiveness === null
                  ? UNRATED.label
                  : getLabelOversightDesignEffectiveness(
                      OversightDesignEffectiveness
                    ) || UNRATED.label,
              OversightPerformanceEffectiveness,
              OversightPerformanceEffectivenessLabelled:
                OversightPerformanceEffectiveness === null
                  ? UNRATED.label
                  : getLabelOversightPerformanceEffectiveness(
                      OversightPerformanceEffectiveness
                    ) || UNRATED.label,
              OversightOverallEffectiveness,
              OversightOverallEffectivenessLabelled:
                OversightOverallEffectiveness === null
                  ? UNRATED.label
                  : getLabelOversightOverallEffectiveness(
                      OversightOverallEffectiveness
                    ) || UNRATED.label,
            };
          })()
        : {};

      // Calculate trend from the first two test results (sorted by date desc)
      const testResults = d.testResults ?? [];
      const currentEffectiveness = testResults[0]?.OverallEffectiveness;
      const previousEffectiveness = testResults[1]?.OverallEffectiveness;
      const effectivenessTrend = calculateTrend(
        currentEffectiveness,
        previousEffectiveness
      );

      const associations = d.parents
        .map((p) => {
          if (!p?.parent?.ObjectType) {
            return;
          }
          const entityInfo = getEntityInfo(p?.parent?.ObjectType);

          return {
            label: `${
              p.parent.ObjectType && p.parent.SequentialId
                ? `${getFriendlyId(p.parent.ObjectType, p.parent.SequentialId)}: `
                : ''
            }${entityInfo.getTitle?.(p) ?? '-'} (${entityInfo.singular})`,
            url: entityInfo.url(p.parent.Id),
          };
        })
        .filter(notEmpty);

      return {
        ...d,
        ControlTypeLabelled: (d.Type as Control_Type_Enum) ?? '-',
        ParentTitle: associations,
        NextTestDate: d.scheduleState?.DueDate ?? null,
        NextTestOverdueDate: d.scheduleState?.OverdueDate ?? null,
        TestScheduleStatus: getTestScheduleStatus(
          d.scheduleState?.OverdueDate,
          d.scheduleState?.DueDate
        ),
        TestScheduleStatusLabelled:
          getLabelTestScheduleStatus(
            getTestScheduleStatus(
              d.scheduleState?.OverdueDate,
              d.scheduleState?.DueDate
            )
          ) ||
          getTestScheduleStatus(
            d.scheduleState?.OverdueDate,
            d.scheduleState?.DueDate
          ),
        DesignEffectiveness,
        DesignEffectivenessLabelled:
          DesignEffectiveness === null
            ? UNRATED.label
            : getLabelDesignEffectiveness(DesignEffectiveness) || UNRATED.label,
        PerformanceEffectiveness,
        PerformanceEffectivenessLabelled:
          PerformanceEffectiveness === null
            ? UNRATED.label
            : getLabelPerformanceEffectiveness(PerformanceEffectiveness) ||
              UNRATED.label,
        OverallEffectiveness,
        OverallEffectivenessLabelled:
          OverallEffectiveness === null
            ? UNRATED.label
            : getLabelOverallEffectiveness(OverallEffectiveness) ||
              UNRATED.label,
        ...oversightFields,
        OverallEffectivenessHistory: (d.testResults ?? [])
          .slice(1, 7)
          .map((c) => ({
            rating: c.OverallEffectiveness ?? 0,
            id: c.Id,
            testDate: c.TestDate,
          })),
        OverallEffectivenessTrend: effectivenessTrend,
        OverallEffectivenessTrendLabelled:
          getEffectivenessTrendLabel(effectivenessTrend) || '-',
        OwnerName: d.owners,
        OpenIssues: d.open_issue_aggregate.aggregate?.count ?? null,
        IssueCount: d.issues_aggregate.aggregate?.count ?? null,
        LinkedIndicatorCount: d.indicators_aggregate.aggregate?.count ?? 0,
        OpenActions: d.actions_aggregate.aggregate?.count ?? null,
        CreatedByUserName: d.createdByUser?.FriendlyName ?? null,
        ModifiedByUserName: d.modifiedByUser?.FriendlyName ?? null,
        TestFrequency:
          getCommonLookupByValue('frequency', d.schedule?.Frequency)?.label ??
          null,
        SequentialIdLabel: d.SequentialId
          ? getFriendlyId(Parent_Type_Enum.Control, d.SequentialId)
          : '',
        allOwners: getAllOwnersCellValue(d),
        allContributors: getAllContributorsCellValue(d),
        allViewers: getAllViewersCellValue(d),
        LatestRatingDate: d.scheduleState?.LatestDate ?? '-',
        ControlGroups: d.parents
          .filter((c) => c.group)
          .map((c) => ({
            label: c.group?.Title ?? '',
            id: c.group?.Id ?? '',
          })),
      };
    });
  }, [
    canViewOversight,
    getCommonLookupByValue,
    getEffectivenessTrendLabel,
    getEntityInfo,
    getLabelDesignEffectiveness,
    getLabelPerformanceEffectiveness,
    getLabelOverallEffectiveness,
    getLabelOversightDesignEffectiveness,
    getLabelOversightPerformanceEffectiveness,
    getLabelOversightOverallEffectiveness,
    getLabelTestScheduleStatus,
    oversightEffectivenessByControl,
    records,
  ]);
};
